import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  // PlusIcon,
  PencilIcon, 
  TrashIcon, 
  DocumentTextIcon,
  CalendarIcon,
  TagIcon,
  UserIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ShareIcon,
  PlayIcon
} from '@heroicons/react/24/outline'
import { useBlogPosts, BlogPost, createSlug, getYouTubeEmbedUrl, isYouTubeUrl } from '../../../hooks/useFirestore'
import { Timestamp } from 'firebase/firestore'

interface BlogFormData {
  title: string
  content: string
  excerpt: string
  author: string
  status: 'draft' | 'published'
  tags: string[]
  imageUrl: string
  metaDescription: string
  type: 'article' | 'social_link' | 'video'
  videoUrl?: string
  socialLinks: {
    facebook: string
    twitter: string
    tiktok: string
    instagram: string
  }
}

const initialFormData: BlogFormData = {
  title: '',
  content: '',
  excerpt: '',
  author: '',
  status: 'draft',
  tags: [],
  imageUrl: '',
  metaDescription: '',
  type: 'article',
  videoUrl: '',
  socialLinks: {
    facebook: '',
    twitter: '',
    tiktok: '',
    instagram: ''
  }
}

export default function AdminBlog() {
  const { 
    documents: posts, 
    loading, 
    error, 
    getDocuments, 
    addDocument, 
    updateDocument, 
    deleteDocument 
  } = useBlogPosts()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null)
  const [formData, setFormData] = useState<BlogFormData>(initialFormData)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    getDocuments([])
  }, [])

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.author.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || post.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const handleOpenForm = (post?: BlogPost) => {
    if (post) {
      setEditingPost(post)
      setFormData({
        title: post.title,
        content: post.content,
        excerpt: post.excerpt,
        author: post.author,
        status: post.status,
        tags: post.tags || [],
        imageUrl: post.imageUrl || '',
        metaDescription: post.metaDescription || '',
        type: post.type || 'article',
        videoUrl: post.videoUrl || '',
        socialLinks: {
          facebook: post.socialLinks?.facebook || '',
          twitter: post.socialLinks?.twitter || '',
          tiktok: post.socialLinks?.tiktok || '',
          instagram: post.socialLinks?.instagram || ''
        }
      })
    } else {
      setEditingPost(null)
      setFormData(initialFormData)
    }
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingPost(null)
    setFormData(initialFormData)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('🚀 Iniciando guardado de post...', { formData, editingPost })
    setSubmitting(true)

    try {
      // Validación básica
      if (!formData.title.trim()) {
        throw new Error('El título es obligatorio')
      }
      if (!formData.excerpt.trim()) {
        throw new Error('El extracto es obligatorio')
      }
      if (!formData.author.trim()) {
        throw new Error('El autor es obligatorio')
      }
      if (formData.type === 'article' && !formData.content.trim()) {
        throw new Error('El contenido es obligatorio para artículos')
      }
      if (formData.type === 'video' && (!formData.videoUrl || !formData.videoUrl.trim())) {
        throw new Error('La URL del video es obligatoria para posts de video')
      }
      if (formData.type === 'video' && formData.videoUrl && !isYouTubeUrl(formData.videoUrl)) {
        throw new Error('Por favor ingresa una URL válida de YouTube')
      }

      const slug = createSlug(formData.title)
      console.log('📝 Slug generado:', slug)
      
      // Preparar los datos exactamente como en el test exitoso
      const postData: {
        metaDescription?: string;
        imageUrl?: string;
        title: string;
        excerpt: string;
        author: string;
        status: 'published' | 'draft';
        tags: string[];
        slug: string;
        type: 'article' | 'social_link' | 'video';
        publishedAt: Timestamp | null;
        content: string;
        readTime?: number;
        videoUrl?: string;
        socialLinks: {
          facebook?: string;
          twitter?: string;
          tiktok?: string;
          instagram?: string;
        };
      } = {
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim(),
        author: formData.author.trim(),
        status: formData.status,
        tags: formData.tags.filter(tag => tag.trim()),
        slug,
        type: formData.type,
        publishedAt: formData.status === 'published' ? Timestamp.now() : null,
        ...(formData.imageUrl.trim() && { imageUrl: formData.imageUrl.trim() }),
        ...(formData.metaDescription.trim() && { metaDescription: formData.metaDescription.trim() }),
        content: '',
        socialLinks: {
          facebook: formData.socialLinks.facebook,
          twitter: formData.socialLinks.twitter,
          tiktok: formData.socialLinks.tiktok,
          instagram: formData.socialLinks.instagram
        }
      }

      // Agregar contenido según el tipo
      if (formData.type === 'social_link') {
        postData.content = formData.excerpt // Para links sociales, el contenido es igual al extracto
        // Solo agregar socialLinks si hay al menos uno
        const socialLinks = {
          ...(formData.socialLinks.facebook.trim() && { facebook: formData.socialLinks.facebook.trim() }),
          ...(formData.socialLinks.twitter.trim() && { twitter: formData.socialLinks.twitter.trim() }),
          ...(formData.socialLinks.tiktok.trim() && { tiktok: formData.socialLinks.tiktok.trim() }),
          ...(formData.socialLinks.instagram.trim() && { instagram: formData.socialLinks.instagram.trim() })
        }
        if (Object.keys(socialLinks).length > 0) {
          postData.socialLinks = socialLinks
        }
      } else if (formData.type === 'video') {
        postData.content = formData.excerpt // Para videos, el contenido es igual al extracto
        if (formData.videoUrl) {
          postData.videoUrl = getYouTubeEmbedUrl(formData.videoUrl.trim())
        }
        postData.readTime = Math.ceil(formData.excerpt.split(' ').length / 200)
      } else {
        postData.content = formData.content.trim()
        postData.readTime = Math.ceil(formData.content.split(' ').length / 200)
      }

      console.log('📝 Datos finales a guardar:', postData)

      if (editingPost) {
        console.log('✏️ Actualizando post existente...')
        const success = await updateDocument(editingPost.id, postData)
        if (success) {
          console.log('✅ Post actualizado exitosamente')
          handleCloseForm()
        } else {
          throw new Error('No se pudo actualizar el post')
        }
      } else {
        console.log('➕ Creando nuevo post...')
        const docId = await addDocument(postData)
        if (docId) {
          console.log('✅ Post creado exitosamente con ID:', docId)
          handleCloseForm()
        } else {
          throw new Error('No se pudo crear el post')
        }
      }

    } catch (err: any) {
      console.error('❌ Error saving post:', err)
      const errorMessage = err.message || 'Error desconocido al guardar el post'
      alert(`Error: ${errorMessage}`)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (post: BlogPost) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar "${post.title}"?`)) {
      await deleteDocument(post.id)
    }
  }

  const handleTagInput = (value: string) => {
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag)
    setFormData({ ...formData, tags })
  }

  const formatDate = (timestamp: Timestamp | null) => {
    if (!timestamp) return 'No publicado'
    return timestamp.toDate().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gradient mb-2">Gestión del Blog</h1>
          <p className="text-gray-300">Crea artículos y comparte links de redes sociales</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setFormData({ ...initialFormData, type: 'article' })
              handleOpenForm()
            }}
            className="bg-gradient-to-r from-primary to-secondary text-dark font-semibold py-3 px-6 rounded-lg btn-hover flex items-center space-x-2"
          >
            <DocumentTextIcon className="w-5 h-5" />
            <span>Nuevo Artículo</span>
          </button>
          <button
            onClick={() => {
              setFormData({ ...initialFormData, type: 'video' })
              handleOpenForm()
            }}
            className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold py-3 px-6 rounded-lg btn-hover flex items-center space-x-2"
          >
            <PlayIcon className="w-5 h-5" />
            <span>Video YouTube</span>
          </button>
          <button
            onClick={() => {
              setFormData({ ...initialFormData, type: 'social_link' })
              handleOpenForm()
            }}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-3 px-6 rounded-lg btn-hover flex items-center space-x-2"
          >
            <ShareIcon className="w-5 h-5" />
            <span>Link Social</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary/50"
            />
          </div>
          <div className="flex items-center space-x-2">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-primary/50"
            >
              <option value="all">Todos</option>
              <option value="published">Publicados</option>
              <option value="draft">Borradores</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-red-500">⚠️</span>
            <span className="font-semibold">Error de permisos</span>
          </div>
          <p className="mb-3">{error}</p>
          {error.includes('permissions') && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mt-3">
              <p className="text-yellow-400 text-sm">
                💡 <strong>Solución:</strong> Haz logout y vuelve a iniciar sesión para actualizar los permisos.
              </p>
              <button
                onClick={() => {
                  // Trigger logout from auth hook
                  window.location.href = '/login'
                }}
                className="mt-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded text-sm hover:bg-yellow-500/30 transition-colors"
              >
                🚪 Ir a Login
              </button>
            </div>
          )}
        </div>
      )}

      {/* Posts list */}
      <div className="space-y-4">
        {filteredPosts.length === 0 ? (
          <div className="glass rounded-xl p-12 text-center">
            <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No hay posts</h3>
            <p className="text-gray-400 mb-6">Comienza creando tu primer post del blog</p>
            <button
              onClick={() => handleOpenForm()}
              className="bg-gradient-to-r from-primary to-secondary text-dark font-semibold py-3 px-6 rounded-lg btn-hover"
            >
              Crear primer post
            </button>
          </div>
        ) : (
          filteredPosts.map((post) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-xl p-6 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="flex items-center space-x-2">
                      {post.type === 'social_link' ? (
                        <ShareIcon className="w-5 h-5 text-purple-400" />
                      ) : post.type === 'video' ? (
                        <PlayIcon className="w-5 h-5 text-red-400" />
                      ) : (
                        <DocumentTextIcon className="w-5 h-5 text-blue-400" />
                      )}
                      <h3 className="text-xl font-semibold text-white">{post.title}</h3>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      post.status === 'published' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {post.status === 'published' ? 'Publicado' : 'Borrador'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      post.type === 'social_link'
                        ? 'bg-purple-500/20 text-purple-400'
                        : post.type === 'video'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {post.type === 'social_link' ? 'Link Social' : post.type === 'video' ? 'Video' : 'Artículo'}
                    </span>
                  </div>
                  
                  <p className="text-gray-300 mb-3 line-clamp-2">{post.excerpt}</p>
                  
                  {/* Mostrar enlaces sociales si es un post de tipo social_link */}
                  {post.type === 'social_link' && post.socialLinks && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.socialLinks.facebook && (
                        <a 
                          href={post.socialLinks.facebook} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-xs hover:bg-blue-600/30"
                        >
                          <span>📘</span>
                          <span>Facebook</span>
                        </a>
                      )}
                      {post.socialLinks.twitter && (
                        <a 
                          href={post.socialLinks.twitter} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 px-2 py-1 bg-sky-500/20 text-sky-400 rounded text-xs hover:bg-sky-500/30"
                        >
                          <span>𝕏</span>
                          <span>Twitter</span>
                        </a>
                      )}
                      {post.socialLinks.tiktok && (
                        <a 
                          href={post.socialLinks.tiktok} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 px-2 py-1 bg-pink-500/20 text-pink-400 rounded text-xs hover:bg-pink-500/30"
                        >
                          <span>🎵</span>
                          <span>TikTok</span>
                        </a>
                      )}
                      {post.socialLinks.instagram && (
                        <a 
                          href={post.socialLinks.instagram} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 px-2 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 rounded text-xs hover:from-purple-500/30 hover:to-pink-500/30"
                        >
                          <span>📷</span>
                          <span>Instagram</span>
                        </a>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                    <div className="flex items-center space-x-1">
                      <UserIcon className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CalendarIcon className="w-4 h-4" />
                      <span>{formatDate(post.publishedAt)}</span>
                    </div>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex items-center space-x-1">
                        <TagIcon className="w-4 h-4" />
                        <span>{post.tags.slice(0, 3).join(', ')}</span>
                      </div>
                    )}
                    {post.readTime && (
                      <span>{post.readTime} min de lectura</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleOpenForm(post)}
                    className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(post)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Modal Form */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseForm}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-900 rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingPost ? 'Editar Post' : 'Nuevo Post'}
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tipo de post */}
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2">
                    Tipo de publicación
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value="article"
                        checked={formData.type === 'article'}
                        onChange={(e) => setFormData({...formData, type: e.target.value as 'article' | 'social_link' | 'video'})}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg transition-colors ${
                        formData.type === 'article' 
                          ? 'border-blue-500 bg-blue-500/10' 
                          : 'border-gray-600 hover:border-gray-500'
                      }`}>
                        <DocumentTextIcon className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                        <div className="text-center">
                          <div className="font-medium text-white">Artículo</div>
                          <div className="text-sm text-gray-400">Contenido completo</div>
                        </div>
                      </div>
                    </label>
                    
                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value="video"
                        checked={formData.type === 'video'}
                        onChange={(e) => setFormData({...formData, type: e.target.value as 'article' | 'social_link' | 'video'})}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg transition-colors ${
                        formData.type === 'video' 
                          ? 'border-red-500 bg-red-500/10' 
                          : 'border-gray-600 hover:border-gray-500'
                      }`}>
                        <PlayIcon className="w-8 h-8 mx-auto mb-2 text-red-400" />
                        <div className="text-center">
                          <div className="font-medium text-white">Video</div>
                          <div className="text-sm text-gray-400">Video de YouTube</div>
                        </div>
                      </div>
                    </label>
                    
                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value="social_link"
                        checked={formData.type === 'social_link'}
                        onChange={(e) => setFormData({...formData, type: e.target.value as 'article' | 'social_link' | 'video'})}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg transition-colors ${
                        formData.type === 'social_link' 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-gray-600 hover:border-gray-500'
                      }`}>
                        <ShareIcon className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                        <div className="text-center">
                          <div className="font-medium text-white">Link Social</div>
                          <div className="text-sm text-gray-400">Enlace a redes sociales</div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2">
                      Título *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary/50"
                      placeholder="Título del post"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2">
                      Autor *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.author}
                      onChange={(e) => setFormData({...formData, author: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary/50"
                      placeholder="Nombre del autor"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2">
                    Extracto *
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={formData.excerpt}
                    onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary/50"
                    placeholder="Breve descripción del post..."
                  />
                </div>

                {/* Enlaces de redes sociales (solo para social_link) */}
                {formData.type === 'social_link' && (
                  <div className="space-y-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-400 flex items-center space-x-2">
                      <ShareIcon className="w-5 h-5" />
                      <span>Enlaces de Redes Sociales</span>
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-300 mb-2 flex items-center space-x-2">
                          <span>📘</span>
                          <span>Facebook</span>
                        </label>
                        <input
                          type="url"
                          value={formData.socialLinks.facebook}
                          onChange={(e) => setFormData({
                            ...formData, 
                            socialLinks: { ...formData.socialLinks, facebook: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500/50"
                          placeholder="https://facebook.com/..."
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-300 mb-2 flex items-center space-x-2">
                          <span>𝕏</span>
                          <span>X (Twitter)</span>
                        </label>
                        <input
                          type="url"
                          value={formData.socialLinks.twitter}
                          onChange={(e) => setFormData({
                            ...formData, 
                            socialLinks: { ...formData.socialLinks, twitter: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-sky-500/50"
                          placeholder="https://x.com/..."
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-300 mb-2 flex items-center space-x-2">
                          <span>🎵</span>
                          <span>TikTok</span>
                        </label>
                        <input
                          type="url"
                          value={formData.socialLinks.tiktok}
                          onChange={(e) => setFormData({
                            ...formData, 
                            socialLinks: { ...formData.socialLinks, tiktok: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500/50"
                          placeholder="https://tiktok.com/..."
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-300 mb-2 flex items-center space-x-2">
                          <span>📷</span>
                          <span>Instagram</span>
                        </label>
                        <input
                          type="url"
                          value={formData.socialLinks.instagram}
                          onChange={(e) => setFormData({
                            ...formData, 
                            socialLinks: { ...formData.socialLinks, instagram: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50"
                          placeholder="https://instagram.com/..."
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* URL del video (solo para videos) */}
                {formData.type === 'video' && (
                  <div className="space-y-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <h3 className="text-lg font-semibold text-red-400 flex items-center space-x-2">
                      <PlayIcon className="w-5 h-5" />
                      <span>Video de YouTube</span>
                    </h3>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-300 mb-2 flex items-center space-x-2">
                        <span>🎥</span>
                        <span>URL del Video *</span>
                      </label>
                      <input
                        type="url"
                        required
                        value={formData.videoUrl || ''}
                        onChange={(e) => setFormData({...formData, videoUrl: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-red-500/50"
                        placeholder="https://youtube.com/watch?v=... o https://youtu.be/..."
                      />
                      <p className="text-xs text-gray-400 mt-2">
                        Puedes usar cualquier formato de URL de YouTube (youtube.com/watch, youtu.be, etc.)
                      </p>
                      
                      {/* Preview del video si la URL es válida */}
                      {formData.videoUrl && isYouTubeUrl(formData.videoUrl) && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-300 mb-2">Vista previa:</p>
                          <div className="relative w-full max-w-md">
                            <iframe
                              width="100%"
                              height="200"
                              src={getYouTubeEmbedUrl(formData.videoUrl)}
                              title="Vista previa del video"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="rounded-lg"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Contenido (solo para artículos) */}
                {formData.type === 'article' && (
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2">
                      Contenido *
                    </label>
                    <textarea
                      required
                      rows={10}
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary/50"
                      placeholder="Contenido completo del post..."
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2">
                      Tags (separados por comas)
                    </label>
                    <input
                      type="text"
                      value={formData.tags.join(', ')}
                      onChange={(e) => handleTagInput(e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary/50"
                      placeholder="tecnología, desarrollo, innovación"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2">
                      Estado
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value as 'draft' | 'published'})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary/50"
                    >
                      <option value="draft">Borrador</option>
                      <option value="published">Publicado</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2">
                    URL de imagen
                  </label>
                  <input
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary/50"
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                </div>

                {formData.type === 'article' && (
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2">
                      Meta descripción (SEO)
                    </label>
                    <textarea
                      rows={2}
                      value={formData.metaDescription}
                      onChange={(e) => setFormData({...formData, metaDescription: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary/50"
                      placeholder="Descripción para motores de búsqueda..."
                      maxLength={160}
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-6 border-t border-white/10 mt-6 bg-gray-800/50 -mx-6 px-6 pb-6 rounded-b-xl">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-6 py-3 border-2 border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 hover:border-gray-500 transition-all duration-200 font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-3 bg-gradient-to-r from-primary to-secondary text-dark font-bold rounded-lg hover:shadow-lg hover:shadow-primary/25 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none border-2 border-primary/50"
                  >
                    {submitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-dark"></div>
                        <span>Guardando...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>{editingPost ? '✏️ Actualizar Post' : '💾 Crear Post'}</span>
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
